import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { supabase } from "./supabase";

// Default timeout for API requests (30 seconds)
const REQUEST_TIMEOUT = 30000;

// Create an AbortController with timeout
function createTimeoutController(timeout: number = REQUEST_TIMEOUT): { controller: AbortController; timeoutId: NodeJS.Timeout } {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  return { controller, timeoutId };
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    let errorMessage = res.statusText;
    let errorData = null;

    try {
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        errorData = await res.json();
        errorMessage = errorData.message || res.statusText;
      } else {
        errorMessage = await res.text() || res.statusText;
      }
    } catch (e) {
      errorMessage = res.statusText;
    }

    const error = new Error(errorMessage);
    (error as any).response = { data: errorData || { message: errorMessage }, status: res.status };
    throw error;
  }
}

// Get current access token from Supabase, refreshing if needed
async function getAccessToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();

  // If session exists but might be expired, try to refresh it
  if (session) {
    const expiresAt = session.expires_at;
    const now = Math.floor(Date.now() / 1000);

    // If token expires within the next 60 seconds, refresh it
    if (expiresAt && expiresAt - now < 60) {
      const { data: { session: refreshedSession } } = await supabase.auth.refreshSession();
      return refreshedSession?.access_token ?? null;
    }
  }

  return session?.access_token ?? null;
}

// Handle 401 by attempting token refresh and retrying the request
async function handleUnauthorized(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.refreshSession();
  return session?.access_token ?? null;
}

// Overloaded function for both GET (url only) and other methods (method, url, data)
export async function apiRequest<T = any>(url: string): Promise<T>;
export async function apiRequest<T = any>(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<T>;
export async function apiRequest<T = any>(
  methodOrUrl: string,
  url?: string,
  data?: unknown | undefined,
): Promise<T> {
  let method: string;
  let actualUrl: string;

  if (url === undefined) {
    method = 'GET';
    actualUrl = methodOrUrl;
  } else {
    method = methodOrUrl;
    actualUrl = url;
  }

  const headers: HeadersInit = {};

  if (data) {
    headers["Content-Type"] = "application/json";
  }

  // Get Supabase access token
  const accessToken = await getAccessToken();
  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  const { controller, timeoutId } = createTimeoutController();

  try {
    let res = await fetch(actualUrl, {
      method,
      headers,
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
      signal: controller.signal,
    });

    // If we get a 401, try refreshing the token and retrying once
    if (res.status === 401) {
      const newToken = await handleUnauthorized();
      if (newToken) {
        headers["Authorization"] = `Bearer ${newToken}`;
        res = await fetch(actualUrl, {
          method,
          headers,
          body: data ? JSON.stringify(data) : undefined,
          credentials: "include",
          signal: controller.signal,
        });
      }
    }

    await throwIfResNotOk(res);
    return await res.json();
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const headers: HeadersInit = {};

    // Get Supabase access token
    const accessToken = await getAccessToken();
    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
    }

    const { controller, timeoutId } = createTimeoutController();

    try {
      let res = await fetch(queryKey[0] as string, {
        headers,
        credentials: "include",
        signal: controller.signal,
      });

      // If we get a 401, try refreshing the token and retrying once
      if (res.status === 401) {
        if (unauthorizedBehavior === "returnNull") {
          return null;
        }
        const newToken = await handleUnauthorized();
        if (newToken) {
          headers["Authorization"] = `Bearer ${newToken}`;
          res = await fetch(queryKey[0] as string, {
            headers,
            credentials: "include",
            signal: controller.signal,
          });
        }
      }

      await throwIfResNotOk(res);
      return await res.json();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: true,
      staleTime: 30 * 1000,
      retry: 2,  // Retry failed requests up to 2 times
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    },
    mutations: {
      retry: 1,  // Retry failed mutations once
    },
  },
});
